import styled from "styled-components/macro";
import { TextInput } from "@src/components/elements";
import React, { useState } from "react";
import { AddressAutocompleteInput } from "@src/components/modules/AddressAutocompleteInput/AddressAutocompleteInput";
import { AddressFormFieldsType } from "@src/components/modules/AddressForm/addressFormFieldsType";

type IntakeAddressFieldValue = Partial<AddressFormFieldsType>;

const StyleRoot = styled.div<{ error?: string }>`
  max-width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: auto;
  grid-template-areas:
    "address1 address1"
    "address2 address2"
    "city state"
    "postalCode country";
  grid-gap: 0 0.5rem;
  padding: ${(props) => (props.error ? "0.5rem" : "0")};
  border: 1px solid ${(props) => (props.error ? "red" : "transparent")};

  .address1 {
    grid-area: address1;
  }
  .address2 {
    grid-area: address2;
  }
  .city {
    grid-area: city;
  }
  .state {
    grid-area: state;
  }
  .postalCode {
    grid-area: postalCode;
  }
  .country {
    grid-area: country;
  }

  .error {
    color: red;
    font-size: 0.875rem;
    margin-top: 0.5rem;
  }
`;

export const IntakeAddressField = ({
  onChange,
  value,
  error,
  className,
  isDisabled,
}: {
  onChange: (newValue: IntakeAddressFieldValue) => void;
  value: IntakeAddressFieldValue | null;
  error: string | undefined;
  className?: string;
  isDisabled?: boolean;
}) => {
  const [address1, setAddress1] = useState(value?.address1);
  const [address2, setAddress2] = useState(value?.address2);
  const [city, setCity] = useState(value?.city);
  const [state, setState] = useState(value?.state);
  const [postalCode, setPostalCode] = useState(value?.postalCode);
  const [country, setCountry] = useState(value?.country);

  const broadcastChange = (inAddress: IntakeAddressFieldValue) => {
    onChange({
      address1,
      address2,
      city,
      state,
      postalCode,
      country,
      ...inAddress,
    });
  };

  return (
    <StyleRoot error={error} className={className}>
      <AddressAutocompleteInput
        inputProps={{
          hideError: true,
          className: "address1",
          name: "address1",
          label: "Address 1",
          labelType: "floating",
          value: address1,
          onChange: (e) => {
            setAddress1(e.target.value);
            broadcastChange({
              address1: e.target.value,
            });
          },
          disabled: isDisabled,
        }}
        onAddressSelected={(address) => {
          setAddress1(address.address1);
          setAddress2(address.address2);
          setCity(address.city);
          setState(address.state);
          setPostalCode(address.postalCode);
          setCountry(address.country);
          broadcastChange(address);
        }}
      />
      <TextInput
        hideError={true}
        className="address2"
        name="address2"
        label="Address 2"
        labelType="floating"
        value={address2}
        onChange={(e) => {
          setAddress2(e.target.value);
          broadcastChange({
            address2: e.target.value,
          });
        }}
        disabled={isDisabled}
      />
      <TextInput
        hideError={true}
        className="city"
        name="city"
        label="City"
        labelType="floating"
        value={city}
        onChange={(e) => {
          setCity(e.target.value);
          broadcastChange({
            city: e.target.value,
          });
        }}
        disabled={isDisabled}
      />
      <TextInput
        hideError={true}
        className="state"
        name="state"
        label="State"
        labelType="floating"
        value={state}
        onChange={(e) => {
          setState(e.target.value);
          broadcastChange({
            state: e.target.value,
          });
        }}
        disabled={isDisabled}
      />
      <TextInput
        hideError={true}
        className="postalCode"
        name="postalCode"
        label="Zip"
        labelType="floating"
        value={postalCode}
        onChange={(e) => {
          setPostalCode(e.target.value);
          broadcastChange({
            postalCode: e.target.value,
          });
        }}
        disabled={isDisabled}
      />
      <TextInput
        hideError={true}
        className="country"
        name="country"
        label="Country"
        labelType="floating"
        value={country}
        onChange={(e) => {
          setCountry(e.target.value);
          broadcastChange({
            country: e.target.value,
          });
        }}
        disabled={isDisabled}
      />

      <div className="error">{error ?? <>&nbsp;</>}</div>
    </StyleRoot>
  );
};
